import { useMemo } from 'react';

import { monorepoRoutePaths, routePaths as adminRoutePaths } from '@apps/admin/route.paths';
import type { DashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import { useDashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import type { GetTranslation, RoutesContextProps } from '@shared/core';
import { useRouterHelper, useTranslation } from '@shared/core';
import { Bell, Clock, CustomCard, Email, Emails, Invitation, NewNote, Pencil, SaveTheDate, Send, WelcomeSign } from '@withjoy/joykit/icons';

import type { ProductVerticals } from '@shared/core/productVerticals/productVerticals.types';
import type { ProductRow } from '../../PrimaryDashboardContent/PrimaryDashboardContent.types';
import type { ProductRowItemProps } from '../../PrimaryDashboardContent/ProductRowItem';
import type { AdminDashboardV2Translations } from '@locales/generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useSmartRouting } from '@shared/hooks/useSmartRouting';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useMessagingDraftCounts } from '@apps/admin/message/shared/useMessagingDraftCounts';
import { STATIONERY_TELEMETRY_SECTION, toProductRowSubItem, useStationeryMenu } from '../useStationeryMenu/useStationeryMenu';

const getCardTilesByCategory = (args: {
  routeHelpers: RoutesContextProps;
  t2: GetTranslation<AdminDashboardV2Translations<string>>;
  telemetry: DashboardTelemetry;
  contextualPath: ReturnType<typeof useSmartRouting> | null;
}): ProductRowItemProps[] => {
  const { routeHelpers, t2, telemetry, contextualPath } = args;

  const { invites: invitesTranslations } = t2('sections');
  const invitesStrings = invitesTranslations;

  const links = [
    {
      id: 'stationery-saveTheDates',
      title: invitesStrings.children.saveTheDates.title,
      icon: SaveTheDate,
      hideDropdownLabel: true,
      tileBehavior: 'openDropdown' as const,
      onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'saveTheDates', section: fromDropdown ? 'Messaging' : '', categoryArg }),
      subItems: [
        {
          title: invitesStrings.children.saveTheDates.children.print.title,
          path: routeHelpers.buildPath('/cards/wedding/save-the-date'),
          pathIsInJoyWeb: true,
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'saveTheDates.paper', section: fromDropdown ? 'Messaging' : '', categoryArg }),
        },
        {
          // TODO: Perform in-app navigation when we push new ecard editing experience to users
          // https://withjoy.atlassian.net/browse/PRNT-843
          title: invitesStrings.children.saveTheDates.children.digital.title,
          path: routeHelpers.buildPath(monorepoRoutePaths.eCardSaveTheDate),
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'saveTheDates.digital', section: fromDropdown ? 'Messaging' : '', categoryArg }),
          telemetryName: monorepoRoutePaths.eCardSaveTheDate,
        },
      ],
    },
    {
      id: 'stationery-invitations',
      title: invitesStrings.children.invitations.title,
      icon: Invitation,
      hideDropdownLabel: true,
      tileBehavior: 'openDropdown' as const,
      onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'invitations', section: fromDropdown ? 'Messaging' : '', categoryArg }),
      subItems: [
        {
          title: invitesStrings.children.invitations.children.print.title,
          path: routeHelpers.buildPath('/cards/wedding/invitation'),
          pathIsInJoyWeb: true,
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'invitations.paper', section: fromDropdown ? 'Messaging' : '', categoryArg }),
        },
        {
          // TODO: Perform in-app navigation when we push new ecard editing experience to users
          // https://withjoy.atlassian.net/browse/PRNT-843
          title: invitesStrings.children.invitations.children.digital.title,
          path: routeHelpers.buildPath(monorepoRoutePaths.eCardInvitations),
          onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'invitations.digital', section: fromDropdown ? 'Messaging' : '', categoryArg }),
          telemetryName: monorepoRoutePaths.eCardInvitations,
        },
      ],
    },
  ];

  const contextualLinks = [
    {
      id: 'stationery-saveTheDates-contextual',
      title: invitesStrings.children.saveTheDates.title,
      icon: SaveTheDate,
      hideDropdownLabel: true,
      path: contextualPath?.saveTheDate ?? undefined,
      pathIsInJoyWeb: true,
      onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'saveTheDates.paper', section: fromDropdown ? 'Messaging' : '', categoryArg }),
    },
    {
      id: 'stationery-invitations-contextual',
      title: invitesStrings.children.invitations.title,
      icon: Invitation,
      hideDropdownLabel: true,
      path: contextualPath?.invitation ?? undefined,
      pathIsInJoyWeb: true,
      onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'invitations.paper', section: fromDropdown ? 'Messaging' : '', categoryArg }),
    },
  ];

  return [...(contextualPath ? contextualLinks : links)];
};

export const useInvitationsProductRow = (args: {
  productVerticals: ProductVerticals;
  hotelBlockReminderEnabled: boolean;
  enableRsvpReminderMessage: boolean;
  enableContactCollectionMessage: boolean;
  isSmsChannelEnabled: boolean;
  eventId?: string;
}): ProductRow => {
  const { productVerticals, hotelBlockReminderEnabled, enableRsvpReminderMessage, isSmsChannelEnabled } = args;
  const routeHelpers = useRouterHelper();
  const telemetry = useDashboardTelemetry();
  const { t2, tq } = useTranslation('adminDashboardV2');
  const { eventInfo } = useEventInfo();
  const stationeryMenu = useStationeryMenu();

  // Mirror the messaging section tabs (Create / Drafts / Scheduled / Sent): the flags gate which
  // sub-items render, and the shared count hook supplies the same batch counts surfaced as
  // right-aligned badges. See MessagingTabNav / useMessagingDraftCounts.
  const [messageDraftsEnabled] = useIsFeatureEnabled('messageDraftsEnabled');
  const [messageSchedulingEnabled] = useIsFeatureEnabled('messageSchedulingEnabled');
  // cache-first: this feeds the globally-mounted burger menu (via useProductRows), so avoid an
  // unconditional background refetch on every admin page. The Messaging tabs keep the cache fresh.
  const { draftCount, scheduledCount, failedScheduledCount } = useMessagingDraftCounts({ eventId: args.eventId, fetchPolicy: 'cache-first' });

  const useContextualLinks = eventInfo?.featureFlags?.includes('useModernEcards');
  const smartRoutingResult = useSmartRouting({ routeHelpers, skip: !useContextualLinks });
  const contextualPath = useContextualLinks && !smartRoutingResult.isLoading ? smartRoutingResult : null;

  const invitationsProductRow = useMemo<ProductRow>(() => {
    const { invites: invitesTranslations } = t2('sections');
    const invitesStrings = invitesTranslations;

    const emailTile: ProductRowItemProps =
      hotelBlockReminderEnabled || enableRsvpReminderMessage || isSmsChannelEnabled
        ? {
            id: 'stationery-messaging',
            title: invitesStrings.children.messaging.title,
            icon: Email,
            hideDropdownLabel: true,
            tileBehavior: 'openDropdown',
            onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'messaging', section: fromDropdown ? 'Messaging' : '', categoryArg }),
            subItems: [
              {
                title: tq`Create`,
                icon: NewNote,
                path: routeHelpers.buildPath(adminRoutePaths.messaging.path),
                onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'messaging.create', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                telemetryName: adminRoutePaths.messaging.path,
              },
              ...(messageDraftsEnabled
                ? [
                    {
                      title: tq`Drafts`,
                      icon: Pencil,
                      count: draftCount > 0 ? draftCount : undefined,
                      path: routeHelpers.buildPath(adminRoutePaths.messagingDrafts.path),
                      onClick: (categoryArg?: string, fromDropdown?: boolean) =>
                        telemetry.openPage({ label: 'messaging.drafts', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                      telemetryName: adminRoutePaths.messagingDrafts.path,
                    },
                  ]
                : []),
              ...(messageDraftsEnabled && messageSchedulingEnabled
                ? [
                    {
                      title: tq`Scheduled`,
                      icon: Clock,
                      // Mirror the Scheduled tab: failed-scheduled messages surface as a red warning
                      // pill with their count; otherwise the neutral scheduled count.
                      count: failedScheduledCount > 0 ? failedScheduledCount : scheduledCount > 0 ? scheduledCount : undefined,
                      countIsWarning: failedScheduledCount > 0,
                      path: routeHelpers.buildPath(adminRoutePaths.messagingScheduled.path),
                      onClick: (categoryArg?: string, fromDropdown?: boolean) =>
                        telemetry.openPage({ label: 'messaging.scheduled', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                      telemetryName: adminRoutePaths.messagingScheduled.path,
                    },
                  ]
                : []),
              {
                // Off-flag the tracking surface keeps its "Tracking" label (matches MessagingTabNav).
                title: messageDraftsEnabled ? tq`Sent` : tq`Tracking`,
                icon: Send,
                path: routeHelpers.buildPath(adminRoutePaths.messagingSent.path),
                onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'messaging.sent', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                telemetryName: adminRoutePaths.messagingSent.path,
              },
            ],
          }
        : {
            id: 'stationery-emails',
            title: invitesStrings.children.emails.title,
            icon: Emails,
            tileBehavior: 'openDropdown',
            onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'emails', section: fromDropdown ? 'Messaging' : '', categoryArg }),
            subItems: [
              {
                title: invitesStrings.children.emails.children.reminder.title,
                icon: Bell,
                path: routeHelpers.buildPath(monorepoRoutePaths.emailCreate + '?draftType=reminder&fromDashboard=true'),
                onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'reminderemail', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                telemetryName: monorepoRoutePaths.emailCreate + '/reminderemail',
              },
              {
                title: invitesStrings.children.emails.children.general.title,
                icon: Email,
                path: routeHelpers.buildPath(monorepoRoutePaths.emailCreate + '?draftType=generic&fromDashboard=true'),
                onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'generalmessage', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                telemetryName: monorepoRoutePaths.emailCreate + '/generalmessage',
              },
              {
                title: invitesStrings.children.customCards.title,
                icon: CustomCard,
                path: routeHelpers.buildPath(monorepoRoutePaths.eCardCustom),
                onClick: (categoryArg?: string, fromDropdown?: boolean) => telemetry.openPage({ label: 'customCards', section: fromDropdown ? 'Messaging' : '', categoryArg }),
                telemetryName: monorepoRoutePaths.eCardCustom,
              },
            ],
          };

    const stationeryTile: ProductRowItemProps = {
      id: 'stationery-dropdown',
      title: tq`Stationery`,
      icon: WelcomeSign,
      hideDropdownLabel: true,
      tileBehavior: 'openDropdown',
      subItems: stationeryMenu.items.map(toProductRowSubItem),
      onClick: (categoryArg?: string, fromDropdown?: boolean) =>
        telemetry.openPage({ label: 'stationery', section: fromDropdown ? STATIONERY_TELEMETRY_SECTION : '', categoryArg }),
    };

    return {
      title: invitesStrings.title,
      isEnabled: !productVerticals.optOutOfInvites,
      children: [
        ...getCardTilesByCategory({
          routeHelpers,
          t2,
          telemetry,
          contextualPath,
        }),
        stationeryTile,
        emailTile,
      ],
    };
  }, [
    t2,
    tq,
    hotelBlockReminderEnabled,
    enableRsvpReminderMessage,
    routeHelpers,
    productVerticals.optOutOfInvites,
    telemetry,
    contextualPath,
    isSmsChannelEnabled,
    stationeryMenu,
    messageDraftsEnabled,
    messageSchedulingEnabled,
    draftCount,
    scheduledCount,
    failedScheduledCount,
  ]);

  return invitationsProductRow;
};
